/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import { ArrowBackRounded, ExitToAppRounded } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import {
  selectAppConfig,
  selectFlightLegsInfo,
  selectHnaOverrideData,
  selectMissionPlanById,
  selectSelectedOverlaidFlightLegId,
} from '../../../store';
import { clearFlightLegsInfo, setSelectedOverlaidFlightLegId } from '../../../store/flightLegs/actions';
import { PERFORMANCE_OUTLOOK_MAP_ID } from '../../../store/maps/actions';
import { cancelRequestHnaOverrideData, clearHnaOverrideData } from '../../../store/missionPlans/actions';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../utils/FeatureFlagUtils';
import MenuItems from '../../common/layout/menu/MenuItems';
import appTheme from '../../common/theme/appTheme.json';
import PerformanceOutlookExportModal from './PerformanceOutlookExportModal';
import GenerateHnaRequestButton from './GenerateHnaRequestButton';

export interface PerformanceOutlookHeaderProps {}

const PerformanceOutlookHeader = (props: PerformanceOutlookHeaderProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [exportModalOpen, setIsExportModalOpen] = useState(false);
  const missionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const flightLegs: FlightLegInfo[] = useSelector(selectFlightLegsInfo, shallowEqual);
  const appConfig = useSelector(selectAppConfig, shallowEqual);
  const selectedOverlaidFlightLegIdPO = useSelector(
    selectSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID),
    shallowEqual,
  );

  const hnaOverrideZones = useSelector(selectHnaOverrideData, shallowEqual);

  //On unmount
  useEffect(() => {
    return () => {
      dispatch(setSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID)(undefined));
      dispatch(clearFlightLegsInfo());
      dispatch(clearHnaOverrideData());
      dispatch(cancelRequestHnaOverrideData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isNil(selectedOverlaidFlightLegIdPO) && flightLegs && flightLegs.length) {
      dispatch(setSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID)(flightLegs[0].id));
    }
  }, [dispatch, selectedOverlaidFlightLegIdPO, flightLegs]);

  //Update plans and count whenever missionPlans from Redux store changes
  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" height="52px">
      <Box display="flex" alignItems="center">
        <StyledPOHeading>Performance Outlook</StyledPOHeading>
      </Box>
      <Box display="flex" alignItems="center" marginRight="16px" justifyContent="space-between" gap="16px">
        <Button
          onClick={() => {
            history.push(`/${MenuItems.plans}/mission-details/`);
          }}
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackRounded />}
        >
          Back to mission details
        </Button>
        {isFeatureEnabled(appConfig, FEATURE_FLAGS.isHnaOverrideButtonEnabled) && (
          <GenerateHnaRequestButton
            hnaZones={hnaOverrideZones}
            missionPlanId={missionPlan.id ?? undefined}
            tailNumber={missionPlan.aircraftTailNum ?? undefined}
            antennaType={missionPlan.antennaType ?? undefined}
          />
        )}
        <Button
          onClick={() => setIsExportModalOpen(true)}
          variant="outlined"
          color="secondary"
          startIcon={<ExitToAppRounded />}
        >
          Export to PDF
        </Button>
        <PerformanceOutlookExportModal
          open={exportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          leg={
            selectedOverlaidFlightLegIdPO !== undefined
              ? flightLegs.find(leg => selectedOverlaidFlightLegIdPO === leg.id)
              : undefined
          }
          totalLegs={flightLegs.length}
        />
      </Box>
    </Box>
  );
};

const StyledPOHeading = styled.h1`
  font-family: ${appTheme.viasatFontFamilyThin};
  color: ${appTheme.primaryTextColor};
  font-size: 28px;
  margin-left: 16px;
  letter-spacing: 0.05rem;
  margin-top: 0px;
  margin-bottom: 0px;

  @media print {
    margin-left: 0px;
  }
`;

export default PerformanceOutlookHeader;
